import React from 'react';
import ReactDOM, {unmountComponentAtNode} from 'react-dom';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {AppBar, Toolbar, Typography, Paper, Button, Card, CardHeader, CardContent, Grid} from '@material-ui/core';
import {AddCircleOutline as AddCircleOutlineIcon, PlayArrow as PlayArrowIcon} from '@material-ui/icons';
import {startCase} from 'lodash/string';
import {isEmpty} from 'lodash/lang';
import {orderBy} from 'lodash/collection';
import {last} from 'lodash/array';
import {CommonModal} from 'components/common';
import {SecondaryNavigation} from 'components/Navigation';
import BadlyWrittenReadingThermometer from '../Reading/BadlyWrittenReadingThermometer';
import ReaderAwardDetail from './ReaderAwardDetail';
import QuizFullPage from './QuizFullPage';
import AwardImageJigsaw from './AwardImageJigsaw';
import {formatErrors} from 'actions/errors';
import {fetchAllAwards} from 'api';
import {fetchAllQuizs, fetchReaderQuizResult} from 'api/quiz';
import {fetchQuizQuestions} from 'api/question';
import * as permissions from 'utils/permissions';
import {gameLevelMap} from 'utils/common';

class ReaderAwardList extends React.Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };
  static propTypes = {};

  constructor(props) {
    super(props);
    this.state = {
      awards: [],
      selectedAward: null,
      quizs: [],
      selectedQuiz: null,
      questions: [],
      readerQuizResults: [],
    };
    this.showAwardModal = this.showAwardModal.bind(this);
    this.closeAwardModal = this.closeAwardModal.bind(this);
    this.showQuizFullPage = this.showQuizFullPage.bind(this);
    this.closeQuizFullPage = this.closeQuizFullPage.bind(this);
  }

  componentDidMount() {
    const {formatErrors, user} = this.props;
    fetchAllAwards(user.level)
      .then(({records}) => {
        this.setState({
          awards: records,
        });
      })
      .catch((err) => formatErrors(err));
    fetchAllQuizs()
      .then(({records}) => {
        this.setState({
          quizs: records,
        });
      })
      .catch((err) => formatErrors(err));
    fetchReaderQuizResult(user.id)
      .then(({records}) => {
        this.setState({
          readerQuizResults: records,
        });
      })
      .catch((err) => formatErrors(err));
  }

  componentDidUpdate() {
    const {questions, selectedQuiz} = this.state;
    const {user} = this.props;
    if (!isEmpty(questions)) {
      ReactDOM.render(
        <QuizFullPage
          quiz={selectedQuiz}
          questions={questions}
          user={user}
          closeQuizFullPage={this.closeQuizFullPage}
        />,
        document.getElementById('quiz-full-page-root'),
      );
    }
  }

  showAwardModal(awardId) {
    const selectedAward = this.state.awards.find((award) => award.id === awardId);
    this.setState({
      selectedAward,
    });
  }

  closeAwardModal() {
    this.setState({
      selectedAward: null,
    });
  }

  showQuizFullPage(quizId) {
    const selectedQuiz = this.state.quizs.find((quiz) => quiz.id === quizId);
    fetchQuizQuestions({quizId}).then(({records}) => {
      this.setState({
        selectedQuiz,
        questions: records,
      });
    });
  }

  closeQuizFullPage() {
    const {user} = this.props;
    this.setState({
      selectedQuiz: null,
      questions: [],
    });
    const fullPageNode = document.getElementById('quiz-full-page-root');
    unmountComponentAtNode(fullPageNode);
    fetchReaderQuizResult(user.id)
      .then(({records}) => {
        this.setState({
          readerQuizResults: records,
        });
      })
      .catch((err) => formatErrors(err));
  }

  render() {
    const {awards, selectedAward, quizs, selectedQuiz, questions, readerQuizResults} = this.state;
    const {user, isMeReader} = this.props;
    const {
      router: {
        history: {push},
      },
    } = this.context;
    return (
      <React.Fragment>
        {isMeReader && (
          <React.Fragment>
            <SecondaryNavigation
              tabs={[
                {url: '/readings', label: 'Journal', isActive: false},
                {url: '/tips', label: 'Tips', isActive: false},
                {url: 'http://blog.kluwell.com/', label: 'Resources', isActive: false},
                {url: '/awards', label: 'Awards', isActive: true},
              ]}
            />
            <BadlyWrittenReadingThermometer score={user.totalReadingDates} />
          </React.Fragment>
        )}
        <CommonModal
          title={selectedAward ? `Congratulations on reading for ${selectedAward.points} nights!` : ''}
          onClose={this.closeAwardModal}
          show={selectedAward !== null}
        >
          {selectedAward && <ReaderAwardDetail award={selectedAward} user={user} />}
        </CommonModal>
        <div className="container content-box">
          <AppBar position="static" color="primary" className="section-app-bar">
            <Toolbar>
              <Typography variant="h6">
                {permissions.isReader(user.role) ? `${user.firstName}'s Awards` : "User's Awards"}
              </Typography>
            </Toolbar>
          </AppBar>
          <Paper className="award-expo">
            {isMeReader && (
              <React.Fragment>
                <p>Your Readicator score goes up one point for every day that you read.</p>
                <p>Keep reading to unlock your next award!</p>
                {isMeReader && !user.classroomReadOnly && (
                  <div className="reading-buttons">
                    <Button variant="contained" color="secondary" onClick={() => push('/readings/create')}>
                      <AddCircleOutlineIcon />
                      New Reading
                    </Button>
                    <Button variant="contained" color="secondary" onClick={() => push('/readings/continue')}>
                      <PlayArrowIcon />
                      Continue Reading
                    </Button>
                  </div>
                )}
              </React.Fragment>
            )}
            <div className="award-thumbnails-wrapper">
              {awards.map((award, idx) => {
                const prevAward = idx > 0 ? awards[idx - 1] : null;
                const unlocked = user.totalReadingDates >= award.points;
                const isNext = unlocked
                  ? false
                  : prevAward === null
                  ? true
                  : user.totalReadingDates >= prevAward.points;
                return (
                  <div key={award.id} className="award-thumbnail">
                    {unlocked ? (
                      <img
                        src={`${process.env.SERVER_BASE_URL}${award.image}`}
                        onClick={() => this.showAwardModal(award.id)}
                      />
                    ) : isNext ? (
                      <AwardImageJigsaw totalReadingDates={user.totalReadingDates} award={award} />
                    ) : (
                      <img src="/images/award-locked.jpg" />
                    )}
                    <div style={{padding: 10}}>
                      <Grid container justify="space-between" spacing={1}>
                        <Grid item xs={12}>
                          <span className="award-description">{award.points} Nights</span>
                        </Grid>
                        <Grid item xs={12}>
                          {quizs
                            .filter((quiz) => quiz.readingNights === award.points && quiz.level === award.level)
                            .map((quiz, index) => {
                              if (index === 0) {
                                const results = orderBy(
                                  readerQuizResults.filter((result) => result.quizId === quiz.id),
                                  ['completedAt'],
                                );
                                const result = last(results);
                                const done = result && result.completedAt !== null;
                                return done ? (
                                  <Grid key={quiz.id} container spacing={1} alignItems="center" justify="space-between">
                                    <Grid item>
                                      <Typography variant="body2">
                                        Quiz Result: {result.correctAnswerCnt}/{result.totalQuestionCnt}
                                      </Typography>
                                    </Grid>
                                    {isMeReader && (
                                      <Grid item>
                                        <Button
                                          color="primary"
                                          size="small"
                                          onClick={() => this.showQuizFullPage(quiz.id)}
                                        >
                                          Take it again
                                        </Button>
                                      </Grid>
                                    )}
                                  </Grid>
                                ) : (
                                  isMeReader && (
                                    <Button
                                      key={quiz.id}
                                      variant="contained"
                                      color="primary"
                                      disabled={user.totalReadingDates < quiz.readingNights || isEmpty(quiz.questions)}
                                      onClick={() => this.showQuizFullPage(quiz.id)}
                                    >
                                      Quiz
                                    </Button>
                                  )
                                );
                              } else {
                                return null;
                              }
                            })}
                        </Grid>
                      </Grid>
                      {isMeReader && award.points === 100 && (
                        <Grid container spacing={2} alignItems="center" justify="space-between">
                          <Grid item>Game</Grid>
                          <Grid item>
                            <Button
                              variant="contained"
                              color="primary"
                              className="game-button"
                              disabled={user.totalReadingDates < 100}
                              onClick={() =>
                                window.open(`${location.origin}/games/${gameLevelMap[user.level]}/index.html`)
                              }
                            >
                              {startCase(gameLevelMap[user.level])}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Paper>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {awards, account, users} = state;
  const userId = parseInt(ownProps.userId || ownProps.match.params.userId);
  let user = account.get('user').toJS();
  let totalReadingDates = account.get('totalReadingDates', 0);
  const isMeReader = permissions.isReader(user.role);
  if (!isMeReader) {
    user = users.get('currentUser').toJS();
    totalReadingDates = user.totalReadingDates;
  }
  return {
    userId,
    isMeReader,
    user,
    totalReadingDates,
    award: awards.get('currentAward').toJS(),
  };
};

const mapDispatchToProps = (dispatch) => ({
  formatErrors: (...args) => dispatch(formatErrors(...args)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ReaderAwardList);
